import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, ShieldCheck, LifeBuoy } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const FAQS = [
  {
    q: "How do I get an account?",
    a: "OnSiteComms is a private tool for participating construction companies. Accounts are created by your company administrator, or you can sign up with your work e-mail on the login screen and your administrator will assign your role.",
  },
  {
    q: "I forgot my password. What do I do?",
    a: "Contact your company administrator — they can reset your password from the Admin panel. If you added a Passkey (Face ID / Touch ID), you can keep signing in with it.",
  },
  {
    q: "How do I sign in with Face ID / Passkey?",
    a: "Sign in once with your e-mail and password, then open Profile and tap \"Add Passkey (FaceID)\". From then on, use the FaceID button on the login screen.",
  },
  {
    q: "I am not receiving notifications.",
    a: "Open the app and allow notifications when prompted. On iPhone, also check Settings > Notifications > OnSiteComms. If it still fails, sign out and sign back in to re-register your device.",
  },
  {
    q: "How does voice message translation work?",
    a: "Voice notes and video captions are automatically transcribed and translated between English and Portuguese, so mixed-language crews can communicate naturally.",
  },
  {
    q: "How do I change the app language?",
    a: "Go to the More tab and tap the language option to switch between English and Portugues.",
  },
  {
    q: "How do I delete my account?",
    a: "Open Profile and tap \"Delete my account\". This permanently removes your account, sign-in credentials and push tokens. You can also request deletion by e-mail below.",
  },
  {
    q: "Which devices are supported?",
    a: "iPhone (App Store), Android, and any modern web browser at on-site-comms.com.",
  },
];

/** Public support / FAQ page — used as the App Store support URL. English only. */
export default function SupportPage() {
  return (
    <div className="min-h-screen bg-background pt-safe">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <img src="/onsitecomms-icon.png" alt="OnSiteComms" className="h-10 w-10 rounded-lg object-contain" />
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">
              <span className="text-[#2B3990]">OnSite</span><span className="text-[#C9A03B]">Comms</span> — Support
            </h1>
            <p className="text-xs text-muted-foreground">Help center and frequently asked questions</p>
          </div>
        </div>

        <div className="rounded-xl border bg-card p-4 mb-6 flex items-start gap-3">
          <LifeBuoy className="h-5 w-5 text-[#2B3990] shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            OnSiteComms is a real-time communication platform for construction field teams: job-linked chats,
            photos and videos, voice notes with automatic translation, PDF markup and task tracking.
          </div>
        </div>

        <h2 className="font-display font-semibold text-lg mb-2">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="mb-8" data-testid="support-faq">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`}>
              <AccordionTrigger className="text-sm text-left" data-testid={`faq-q-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <h2 className="font-display font-semibold text-lg mb-2">Contact us</h2>
        <div className="rounded-xl border bg-card p-4 mb-8 space-y-2 text-sm">
          <a
            href="mailto:jlgarbim5@hotmail.com"
            className="inline-flex items-center gap-2 text-[#2B3990] hover:underline"
            data-testid="support-email-link"
          >
            <Mail className="h-4 w-4" /> jlgarbim5@hotmail.com
          </a>
          <p className="text-muted-foreground text-xs">
            We usually reply within 1 business day (Mon-Fri, 8am-6pm ET).
          </p>
        </div>

        <div className="border-t pt-4 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#2B3990] hover:underline" data-testid="support-back-link">
              <ArrowLeft className="h-4 w-4" /> Back to OnSiteComms
            </Link>
            <Link to="/privacy" className="inline-flex items-center gap-1.5 text-sm text-[#2B3990] hover:underline" data-testid="support-privacy-link">
              <ShieldCheck className="h-4 w-4" /> Privacy Policy
            </Link>
          </div>
          <span className="text-[11px] text-muted-foreground">Copyright © 2026 Johnny Garbim® — All rights reserved.</span>
        </div>
      </div>
    </div>
  );
}
